import am5geodata_ukLow from '@amcharts/amcharts5-geodata/ukLow';

export const UK_LOW_GEO_WITHOUT_REPUBLIC_OF_IRELAND = (() => {
  const collection = am5geodata_ukLow as { type: string; features: { id?: string }[] };
  return {
    type: 'FeatureCollection' as const,
    features: collection.features.filter((f) => f.id !== 'IE'),
  };
})();

const NORMALIZE = (s: string) =>
  s
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const UK_NAME_TO_ID: Record<string, string> = {
  [NORMALIZE('Scotland')]: 'GB-SCT',
  [NORMALIZE('North East England')]: 'GB-UKC',
  [NORMALIZE('North West England')]: 'GB-UKD',
  [NORMALIZE('Yorkshire and the Humber')]: 'GB-UKE',
  [NORMALIZE('East Midlands')]: 'GB-UKF',
  [NORMALIZE('West Midlands')]: 'GB-UKG',
  [NORMALIZE('East of England')]: 'GB-UKH',
  [NORMALIZE('Greater London')]: 'GB-UKI',
  [NORMALIZE('South East England')]: 'GB-UKJ',
  [NORMALIZE('South West England')]: 'GB-UKK',
  [NORMALIZE('Wales')]: 'GB-WLS',
  [NORMALIZE('Northern Ireland')]: 'GB-NIR',
  [NORMALIZE('Guernsey')]: 'GG',
  [NORMALIZE('Jersey')]: 'JE',
  [NORMALIZE('Isle of Man')]: 'IM',
};

const UK_ALIASES: Record<string, string> = {
  scotland: 'GB-SCT',
  wales: 'GB-WLS',
  'northern ireland': 'GB-NIR',
  ni: 'GB-NIR',
  london: 'GB-UKI',
  'greater london': 'GB-UKI',
  'north east': 'GB-UKC',
  'north east england': 'GB-UKC',
  'north west': 'GB-UKD',
  'north west england': 'GB-UKD',
  yorkshire: 'GB-UKE',
  'yorkshire and the humber': 'GB-UKE',
  'east midlands': 'GB-UKF',
  'west midlands': 'GB-UKG',
  'east of england': 'GB-UKH',
  'east england': 'GB-UKH',
  'south east': 'GB-UKJ',
  'south east england': 'GB-UKJ',
  'south west': 'GB-UKK',
  'south west england': 'GB-UKK',
  guernsey: 'GG',
  jersey: 'JE',
  'isle of man': 'IM',
  'bailiwick of guernsey': 'GG',
  'bailiwick of jersey': 'JE',
  alderney: 'GG',
  sark: 'GG',
  herm: 'GG',
};

export function regionLabelToUkMapId(label: string): string | null {
  if (!label) return null;
  const raw = label.trim();
  const asId = raw.toUpperCase();
  if (/^GB-[A-Z0-9]{3}$/.test(asId)) {
    return asId;
  }
  if (asId === 'GG' || asId === 'JE' || asId === 'IM') {
    return asId;
  }

  const key = NORMALIZE(raw);
  if (
    key.includes('channel islands') ||
    (key.includes('guernsey') && key.includes('jersey') && key.includes('bailiwick'))
  ) {
    return null;
  }
  if (UK_NAME_TO_ID[key]) {
    return UK_NAME_TO_ID[key];
  }
  if (UK_ALIASES[key]) {
    return UK_ALIASES[key];
  }
  const collapsed = key.replace(/\s/g, '');
  if (UK_ALIASES[collapsed]) {
    return UK_ALIASES[collapsed];
  }

  if (key.includes('scotland')) return 'GB-SCT';
  if (key.includes('wales') && !key.includes('new south wales')) return 'GB-WLS';
  if (key.includes('northern ireland')) return 'GB-NIR';
  if (key.includes('london')) return 'GB-UKI';
  if (key.includes('yorkshire')) return 'GB-UKE';
  if (key.includes('north east')) return 'GB-UKC';
  if (key.includes('north west')) return 'GB-UKD';
  if (key.includes('east midlands')) return 'GB-UKF';
  if (key.includes('west midlands')) return 'GB-UKG';
  if (key.includes('south west')) return 'GB-UKK';
  if (key.includes('south east')) return 'GB-UKJ';
  if (key.includes('east of england') || key.includes('east england')) return 'GB-UKH';

  if (key.includes('jersey') && !key.includes('new jersey')) return 'JE';
  if (key.includes('guernsey')) return 'GG';

  return null;
}

export function regionGroupToUkPolygonIds(label: string): string[] {
  const key = NORMALIZE(String(label));
  if (!key) return [];
  if (
    key.includes('channel islands') ||
    (key.includes('guernsey') && key.includes('jersey') && key.includes('bailiwick'))
  ) {
    return ['GG', 'JE'];
  }
  const id = regionLabelToUkMapId(String(label).trim());
  return id ? [id] : [];
}

export const UK_MAP_VIEW_GEO_BOUNDS = {
  left: -10.85,
  right: 3.35,
  top: 61.65,
  bottom: 48.1,
} as const;

export function isGwpColumn(columnName: string): boolean {
  return columnName.includes('GWP');
}

export interface MapPolygonDatum {
  id: string;
  value: number;
}

export function buildAggregatedMapData(
  rows: Record<string, unknown>[] | null | undefined,
  column: string
): MapPolygonDatum[] {
  if (!rows?.length || !column) return [];
  const acc = new Map<string, number>();
  for (const row of rows) {
    const group = row['GROUP'];
    if (group === undefined || group === null) continue;
    const ids = regionGroupToUkPolygonIds(String(group));
    if (!ids.length) continue;
    const raw = row[column];
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isNaN(n)) continue;
    for (const id of ids) {
      acc.set(id, (acc.get(id) ?? 0) + n);
    }
  }
  return Array.from(acc.entries()).map(([id, value]) => ({ id, value }));
}
